import type { ChartRegistryEntry, TableColumnDef } from "./types";
import {
  getTopLevelRows,
  getLineRows,
  getMapRows,
  getGenericTableData,
  getTieredBarRows,
  getQuadrantScatterRows,
  getDataTableChartRows,
} from "./data/transformers";
import { renderBar } from "./renderers/BarRenderer";
import { renderLine } from "./renderers/LineRenderer";
import { renderPie } from "./renderers/PieRenderer";
import { renderPieDrilldown } from "./renderers/PieDrilldownRenderer";
import { renderFunnel } from "./renderers/FunnelRenderer";
import { renderForceBar } from "./renderers/ForceBarRenderer";
import { renderTieredBar } from "./renderers/TieredBarRenderer";
import { renderQuadrantScatter } from "./renderers/QuadrantScatterRenderer";
import { renderLineWithBar } from "./renderers/LineWithBarRenderer";
import { renderMap } from "./renderers/MapRenderer";
import { renderGroupedBar } from "./renderers/GroupedBarRenderer";
import { renderSpecialMultiLine } from "./renderers/SpecialMultiLineRenderer";

// ---------------------------------------------------------------------------
// Export item lists — Highcharts built-in menu item keys.
// These appear in the hamburger context button on every chart.
// FORCE_BAR omits CSV/XLS because its data structure doesn't map cleanly to rows.
// ---------------------------------------------------------------------------
const STANDARD_EXPORT = [
  "viewFullscreen",
  "printChart",
  "downloadPNG",
  "downloadJPEG",
  "downloadCSV", // Highcharts built-in — downloads chart series as CSV
  "downloadXLS", // Highcharts built-in — downloads chart series as XLS
];

const STANDARD_EXPORT_PDF = [...STANDARD_EXPORT, "downloadPDF"];

const FORCE_BAR_EXPORT = [
  "viewFullscreen",
  "printChart",
  "downloadPNG",
  "downloadJPEG",
  "downloadPDF",
];

// ---------------------------------------------------------------------------
// Table column definitions — used by ChartTableView when rendering the table
// and by exportTableCsv/exportTableXls for the download output.
// ---------------------------------------------------------------------------

const DEFAULT_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "category", header: "Category" },
  { key: "count", header: "Count", align: "end" },
  { key: "percentage", header: "Percentage", align: "end" },
];

const LINE_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "series", header: "Series" },
  { key: "category", header: "Category" },
  { key: "value", header: "Value", align: "end" },
];

const MAP_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "state", header: "State" },
  { key: "cases", header: "Cases", align: "end" },
];

const TIERED_BAR_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "prescriber", header: "Prescriber", align: "center" },
  { key: "pts", header: "Pts", align: "center" },
  { key: "pa_percentage", header: "PA %", align: "center" },
  { key: "abandon_percentage", header: "Abandon %", align: "center" },
  { key: "tier", header: "Tier", align: "center" },
];

const QUADRANT_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "practice", header: "Practice", align: "center" },
  { key: "pts", header: "Total Cases", align: "center" },
  { key: "pa_cases", header: "PA Cases", align: "center" },
  { key: "pa_approved_cases", header: "PA Approved", align: "center" },
  { key: "pa_percentage", header: "PA %", align: "center" },
  { key: "abandon_percentage", header: "Abandon %", align: "center" },
  // cellType "tier-badge" renders a styled pill instead of plain text in ChartTableView.
  { key: "tier", header: "Tier", align: "center", cellType: "tier-badge" },
];

// Shared row extractor for bar/pie/pie_drilldown/funnel — all use the same
// top-level {category, count, percentage} shape.
const defaultGetTableRows = (data: unknown) =>
  getTopLevelRows(data).map((r) => ({
    category: r.category,
    count: r.count,
    percentage: r.percentage,
  }));

// ---------------------------------------------------------------------------
// REGISTRY — the single source of truth for every chart type.
//
// Each entry describes:
//   renderer      — pure function: (data, spec, title) → Highcharts.Options
//   exportItems   — which Highcharts menu items appear in the context button
//   convertibleTo — chart types the user can switch to from this chart
//   fillHeight    — whether the chart should expand to fill its grid cell
//   constructorType — set to "mapChart" for choropleth maps (different Highcharts constructor)
//   tableColumns  — column definitions shown when the user switches to table view
//   getTableRows  — extracts row data from the API response for the table view
//
// To add a new chart type: create a renderer in src/charts/renderers/, then
// add one entry here. Nothing else needs to change.
// ---------------------------------------------------------------------------
const REGISTRY = new Map<string, ChartRegistryEntry>([
  [
    "bar",
    {
      renderer: renderBar,
      exportItems: STANDARD_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "stacked_bar",
    {
      renderer: renderBar, // same renderer — spec differentiates stacked vs grouped
      exportItems: STANDARD_EXPORT,
      fillHeight: true,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "time_series_bar",
    {
      renderer: renderBar, // same renderer — periods become xAxis categories
      exportItems: STANDARD_EXPORT,
      fillHeight: true,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "pie",
    {
      renderer: renderPie,
      exportItems: STANDARD_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "pie_drilldown",
    {
      renderer: renderPieDrilldown,
      exportItems: STANDARD_EXPORT,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows, // reads series[0].data from the drilldown payload
    },
  ],
  [
    "multi_line",
    {
      renderer: renderLine,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: LINE_TABLE_COLUMNS,
      getTableRows: (data) =>
        getLineRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "line",
    {
      renderer: renderLine, // same renderer as multi_line
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: LINE_TABLE_COLUMNS,
      getTableRows: (data) =>
        getLineRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "special_multi_line",
    {
      renderer: renderSpecialMultiLine,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: LINE_TABLE_COLUMNS,
      getTableRows: (data) =>
        getLineRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "funnel",
    {
      renderer: renderFunnel,
      exportItems: STANDARD_EXPORT_PDF,
      tableColumns: DEFAULT_TABLE_COLUMNS,
      getTableRows: defaultGetTableRows,
    },
  ],
  [
    "force_bar",
    {
      renderer: renderForceBar,
      exportItems: FORCE_BAR_EXPORT, // no CSV/XLS — complex nested data doesn't map to flat rows
      fillHeight: true,
      // No tableColumns — force_bar has no supported conversions
    },
  ],
  [
    "tiered_bar",
    {
      renderer: renderTieredBar,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      tableColumns: TIERED_BAR_TABLE_COLUMNS,
      getTableRows: (data) =>
        getTieredBarRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "quadrant_scatter",
    {
      renderer: renderQuadrantScatter,
      exportItems: STANDARD_EXPORT,
      tableColumns: QUADRANT_TABLE_COLUMNS,
      getTableRows: (data) =>
        getQuadrantScatterRows(data) as unknown as Array<
          Record<string, unknown>
        >,
    },
  ],
  [
    "line_with_bar",
    {
      renderer: renderLineWithBar,
      exportItems: STANDARD_EXPORT_PDF,
      fillHeight: true,
      // No tableColumns — combo chart data doesn't cleanly reduce to bar/pie
    },
  ],
  [
    "map",
    {
      renderer: renderMap,
      exportItems: STANDARD_EXPORT,
      // mapChart uses a different Highcharts constructor (Highcharts.mapChart).
      constructorType: "mapChart",
      tableColumns: MAP_TABLE_COLUMNS,
      getTableRows: (data) =>
        getMapRows(data) as unknown as Array<Record<string, unknown>>,
    },
  ],
  [
    "grouped_bar",
    {
      renderer: renderGroupedBar,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "custom_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "custom_table",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "deviation_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "average_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "cards",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "info_bar",
    {
      renderer: () => null,
      exportItems: FORCE_BAR_EXPORT,
      fillHeight: true,
    },
  ],
  [
    "data_table",
    {
      // data_table is a raw API table — no Highcharts chart is rendered.
      // renderer returns null; DashboardWidgetContent routes this to DataTableWidget.
      renderer: () => null,
      exportItems: STANDARD_EXPORT,
      getTableRows: (data) => getGenericTableData(data).rows,
    },
  ],
]);

// ---------------------------------------------------------------------------
// CHART_CONVERSIONS — single source of truth for all chart-type conversions.
//
// Each key lists the chart types a user can switch to from that source.
// "table" means the built-in table view (not the data_table widget).
//
// Comment legend:
//   ↔  bidirectional  — both A→B and B→A are declared (symmetric round-trip)
//   →  one-way only   — the reverse direction is intentionally absent
//
// To add a new conversion: append the target to the source's array.
// For a bidirectional pair: add to BOTH sides and mark both ↔.
// Chart types with no entry have zero conversions (stacked_bar, force_bar, etc.).
// ---------------------------------------------------------------------------
const CHART_CONVERSIONS: Record<string, string[]> = {
  // ↔ bar ↔ pie — identical data shape, full round-trip
  bar: ["pie", "table"],
  pie: ["bar", "table"],

  // → pie_drilldown → bar  (top-level slices map to bar categories)
  // → pie_drilldown → table
  pie_drilldown: ["bar", "table"],

  // → funnel → bar  (same {category, count} shape)
  //   bar → funnel NOT offered — funnel is always the originating source
  funnel: ["bar"],

  // ↔ map ↔ bar, ↔ map ↔ pie — state rows map to category/count for both targets
  //   bar → map and pie → map NOT listed — they originate from map, not the reverse
  map: ["bar", "pie", "table"],

  // → data_table → bar / pie  (raw API table visualised as a chart on demand)
  //   bar / pie → data_table NOT offered — data_table is always the originating source
  data_table: ["bar", "pie"],

  // → table-only (no meaningful chart↔chart conversion for these types)
  multi_line: ["table"],
  special_multi_line: ["table"],
  line: ["table"],
  tiered_bar: ["table"],
  quadrant_scatter: ["table"],

  // No conversions — complex / composite data that doesn't reduce to flat rows:
  //   stacked_bar     — multi-series grouped/stacked
  //   time_series_bar — xAxis is dates, not plain categories
  //   force_bar       — nested hierarchy, can't flatten to rows
  //   line_with_bar   — dual-axis combo
};

// ---------------------------------------------------------------------------
// Public accessors
// ---------------------------------------------------------------------------

export function getChartEntry(
  chartType: string,
): ChartRegistryEntry | undefined {
  return REGISTRY.get(chartType);
}

export function getChartRenderer(chartType: string) {
  return REGISTRY.get(chartType)?.renderer;
}

// Returns the chart types a user can convert to from `chartType`.
// Reads from CHART_CONVERSIONS — the single place to configure all conversions.
export function getConvertibleTargets(chartType: string): string[] {
  return CHART_CONVERSIONS[chartType] ?? [];
}

// Whether a chart/table can be converted to a Highcharts chart.
// data_table needs at least one numeric column to chart; line types can't be charted
// from table view because the multi-series data structure doesn't map to bar/pie rows.
export function canConvertToChart(sourceType: string, data: unknown): boolean {
  if (sourceType === "data_table") {
    return getDataTableChartRows(data).length > 0;
  }
  return (
    sourceType !== "line" &&
    sourceType !== "multi_line" &&
    sourceType !== "special_multi_line"
  );
}
